import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 30;      // ~60s total
const POLL_INTERVAL = 2000;

/**
 * PaymentSuccess — polls /payments/status until paid|expired|failed.
 * Never advances past success without confirming payment_status === 'paid'.
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('polling'); // polling | paid | failed | expired | timeout | notfound
  const [record, setRecord] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState('notfound'); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        setRecord(res.data);
        if (res.data.payment_status === 'paid') { setState('paid'); return; }
        if (['failed', 'expired', 'refunded'].includes(res.data.payment_status)) {
          setState(res.data.payment_status); return;
        }
      } catch (e) {
        if (e?.response?.status === 404) { setState('notfound'); return; }
      }
      if (attemptsRef.current >= MAX_POLLS) { setState('timeout'); return; }
      setTimeout(poll, POLL_INTERVAL);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
      <div
        className="max-w-md w-full rounded-3xl p-8 border border-white/10"
        style={{ background: 'linear-gradient(180deg,rgba(20,20,25,.92),rgba(10,10,14,.98))', backdropFilter: 'blur(24px)' }}
        data-testid="payment-success-page"
      >
        {state === 'polling' && (
          <div className="flex flex-col items-center gap-4 text-center">
            <Loader2 className="w-14 h-14 animate-spin text-cyan-400" />
            <h1 className="text-2xl font-light tracking-tight">Confirmando pagamento…</h1>
            <p className="text-white/50 text-sm">Isso leva alguns segundos. Não feche esta página.</p>
          </div>
        )}
        {state === 'paid' && (
          <div className="flex flex-col items-center gap-4 text-center" data-testid="payment-paid">
            <CheckCircle2 className="w-16 h-16 text-emerald-400" />
            <h1 className="text-3xl font-light tracking-tight">Pagamento aprovado</h1>
            <p className="text-white/60 text-sm">
              Sua compra foi confirmada e o benefício já foi liberado na sua conta WClub.
            </p>
            <button
              onClick={() => navigate('/')}
              data-testid="payment-back-home"
              className="mt-6 px-8 py-3 rounded-full bg-white text-black text-sm tracking-wide font-medium hover:bg-white/90 transition"
            >
              Voltar ao WClub
            </button>
          </div>
        )}
        {(state === 'failed' || state === 'expired' || state === 'timeout' || state === 'notfound' || state === 'refunded') && (
          <div className="flex flex-col items-center gap-4 text-center" data-testid="payment-error">
            <XCircle className="w-14 h-14 text-rose-400" />
            <h1 className="text-2xl font-light tracking-tight">
              {state === 'timeout' ? 'Confirmação demorou demais' :
               state === 'notfound' ? 'Sessão não encontrada' :
               state === 'refunded' ? 'Pagamento reembolsado' :
               state === 'expired' ? 'Sessão expirada' : 'Pagamento não concluído'}
            </h1>
            <p className="text-white/50 text-sm">
              {state === 'timeout'
                ? 'Se o valor foi debitado, o crédito aparece automaticamente em minutos. Verifique seu Wcash.'
                : 'Você pode tentar novamente.'}
            </p>
            <button
              onClick={() => navigate('/')}
              data-testid="payment-back-home"
              className="mt-6 px-8 py-3 rounded-full bg-white/10 text-white text-sm tracking-wide font-medium hover:bg-white/15 transition"
            >
              Voltar ao WClub
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
